import Button from 'ext-ui/components/Button/Button'
import Tooltip from 'ext-ui/components/Tooltip/Tooltip'
import {
  base_colorAction,
  base_colorRaisedSurface,
  base_contentSelected,
  cta_linkTextAlert,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { font_family_weight_regular } from 'ext-ui/style/typography'
import React, { memo, useCallback } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import SvgFlag from 'src/components/Svgs/SvgFlag'
import SvgCheck from 'src/components/Svgs/SvgNewCheck'
import { useTranslate } from 'src/localization/Trns'
import { TransactionManagerReviewStatus } from 'src/models/transaction.models'
import { css } from 'styled-components'

interface ApprovalActionsProps {
  txId: string
  onReview: (txId: string, status: TransactionManagerReviewStatus) => void
}

const Container = memo(function Container({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <div
      css={css`
        display: flex;
        gap: ${spacing(2)};
        padding: ${spacing(1)};
        align-items: center;
        border-radius: ${spacing(2)};
        background: ${base_colorRaisedSurface};

        .no-shell {
          font-family: ${font_family_weight_regular};
          padding: ${spacing(2)} ${spacing(4)};
          &:hover {
            background-color: ${base_contentSelected};
          }
        }
      `}
    >
      {children}
    </div>
  )
})

export const ApprovalActions = memo(function ApprovalActions({
  txId,
  onReview,
}: ApprovalActionsProps) {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()

  const approve = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      analytics?.track('manager review actioned txn', {
        action: 'approved',
      })
      onReview(txId, TransactionManagerReviewStatus.Reviewed)
    },
    [onReview, txId, analytics],
  )

  const reject = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      analytics?.track('manager review actioned txn', {
        action: 'rejected',
      })
      onReview(txId, TransactionManagerReviewStatus.Rejected)
    },
    [onReview, txId, analytics],
  )

  return (
    <Container>
      <Tooltip tooltipContent={translate('Review')}>
        <Button
          variant="no-shell"
          dropShadow={false}
          className="no-shell"
          iconLeft={<SvgCheck stroke={base_colorAction} />}
          onClick={approve}
          data-testid="review-single"
        />
      </Tooltip>
      <Tooltip tooltipContent={translate('Flag')}>
        <Button
          variant="no-shell"
          dropShadow={false}
          className="no-shell"
          iconLeft={<SvgFlag stroke={cta_linkTextAlert} />}
          // avoid inline style object; use css or className
          css={css`
            color: ${cta_linkTextAlert};
          `}
          onClick={reject}
          data-testid="flag-single"
        />
      </Tooltip>
    </Container>
  )
})
